import { captureException } from '@sentry/react';
import { Button } from 'components/Button';
import { SelectInput, SelectOption } from 'components/Forms';
import { Heading } from 'components/Typography';
import { getAcceptedInsuranceOptions } from 'domains/signup/insurance/data/insurance';
import { InsuranceContext, withInsuranceMachineState } from 'domains/signup/insurance/data/insuranceEntryContext';
import { isUndefined, noop } from 'lodash';
import { ChangeEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { StateAbbrev } from 'utils/states';

type Props = {
  defaultValue?: string;
  onSubmit: (value: string) => void;
  medicaidProviderOptions?: SelectOption[];
};

const EnterMedicaidProvider: React.FC<Props> = ({ defaultValue, onSubmit, medicaidProviderOptions }) => {
  const [value, setValue] = useState(defaultValue);

  const { t } = useTranslation();

  return (
    <>
      <Heading level="h2" className="mb-16">
        {t('signup.direct.steps.enterMedicaidProvider.title')}
      </Heading>
      <Heading level="h7" className="mb-64 sm-d:mb-40">
        {t(`signup.direct.steps.enterMedicaidProvider.subtitle`)}
      </Heading>

      <SelectInput
        label={t('screener.questions.singleSelectTitle')}
        allowEmpty
        options={medicaidProviderOptions ?? []}
        name="state"
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => setValue(e.target.value)}
        clearValue={noop}
        setError={noop}
        setValue={noop}
        disabled={isUndefined(medicaidProviderOptions)}
      />

      <Button
        size="lg"
        className="mt-64 sm-d:mt-40 sm-d:w-full"
        onClick={() => {
          onSubmit(value as string);
        }}
        disabled={isUndefined(value) || value === ''}
      >
        {t('signup.direct.steps.enterMedicaidProvider.submit')}
      </Button>
    </>
  );
};

const InsuranceWrapper = () => {
  const state = InsuranceContext.useSelector(({ context }) => context.state);
  const medicaidProvider = InsuranceContext.useSelector(({ context }) => context.medicaidProvider);
  const { send } = InsuranceContext.useActorRef();

  const [insuranceOptions, setInsuranceOptions] = useState<SelectOption[]>();

  useEffectOnce(() => {
    getAcceptedInsuranceOptions(state as StateAbbrev)
      .then(o => setInsuranceOptions(o))
      .catch(e => {
        // @note(juan): This shouldn't happen but it can happen if the contentful data is broken by accident.
        // In order to allow the user to continue signing up we route them to private pay but also log the error
        captureException(e, {
          extra: {
            message: `Failed to load Medicaid provider list from contentful for state code ${state}`,
          },
        });

        send({
          type: 'medicaidProviderError',
        });
      });
  });

  const onSubmit = useCallback(
    (value: string) => {
      send({
        type: 'submitMedicaidProvider',
        medicaidProvider: value as string,
      });
    },
    [send],
  );

  return (
    <EnterMedicaidProvider
      defaultValue={medicaidProvider}
      onSubmit={onSubmit}
      medicaidProviderOptions={insuranceOptions}
    />
  );
};

export const InsuranceEnterMedicaidProvider = withInsuranceMachineState(InsuranceWrapper, 'enterMedicaidProvider');
